import { useEffect, useRef } from 'react';
import {
  CATALOGUE,
  CATEGORY_ORDER,
  getActiveSources,
  isRenderable,
  resolveOpacity,
} from '../data/catalogue';
import { createArcgisImageMapType } from './adapters/arcgisImageMapType';
import { createArcgisTileMapType } from './adapters/arcgisTileMapType';
import { createWmsImageMapType } from './adapters/wmsImageMapType';
import { createHealthTracker } from './layerHealth';

const DEFAULT_MAX_ZOOM = 20;

function normaliseRegion(region) {
  return region === 'england' || region === 'scotland' || region === 'wales' ? region : null;
}

function categoryIndex(categoryId) {
  const index = CATEGORY_ORDER.indexOf(categoryId);
  return index === -1 ? CATEGORY_ORDER.length : index;
}

function removeOverlay(map, overlay) {
  const overlays = map.overlayMapTypes;
  // Always re-read the index at call time — MVCArray.removeAt shifts higher
  // indices down, so a cached index would remove the wrong overlay.
  const index = overlays.getArray().indexOf(overlay);
  if (index >= 0) overlays.removeAt(index);
}

function createOverlay(source, onOutcome) {
  const opacity = resolveOpacity(source);

  if (source.kind === 'wms') {
    return createWmsImageMapType(source, opacity, onOutcome);
  }

  if (source.kind === 'arcgis-rest') {
    return createArcgisImageMapType(source, opacity, onOutcome);
  }

  // Pre-rendered fused cache, which is all the Hutton replacements publish.
  if (source.kind === 'arcgis-tiles') {
    return createArcgisTileMapType(source, opacity, onOutcome);
  }

  return null;
}

/**
 * Collect the ordered list of sources that should be rendered right now.
 * Enforces both minZoom and maxZoom (defaulting to DEFAULT_MAX_ZOOM).
 */
function collectDesiredSources(layerStates, viewportRegion, zoom) {
  const region = normaliseRegion(viewportRegion);
  if (!region) return [];

  const desired = [];

  for (const category of CATALOGUE) {
    const state = layerStates[category.id];
    if (!state?.visible) continue;

    const sources = getActiveSources(category, region).filter((source) => {
      const maxZoom = source.maxZoom ?? DEFAULT_MAX_ZOOM;
      return isRenderable(source) && zoom >= source.minZoom && zoom <= maxZoom;
    });

    sources.forEach((source, sourceIndex) => {
      desired.push({
        categoryId: category.id,
        sourceIndex,
        source,
      });
    });
  }

  return desired.sort((a, b) => {
    const precedenceDelta = (a.source.precedence ?? 100) - (b.source.precedence ?? 100);
    if (precedenceDelta !== 0) return precedenceDelta;

    const categoryDelta = categoryIndex(a.categoryId) - categoryIndex(b.categoryId);
    if (categoryDelta !== 0) return categoryDelta;

    return a.sourceIndex - b.sourceIndex;
  });
}

/**
 * For all visible, renderable sources in the current region, compute the zoom
 * the map should move to so that at least one of them can draw. Returns null if
 * no adjustment is needed.
 *
 * Zooming IN takes priority and is unchanged: the shallowest minZoom among
 * sources hidden because the map is too far out.
 *
 * Zooming OUT is the mirror case, and only runs when nothing needs zooming in.
 * A source with a maxZoom is capped for a reason the catalogue states (the three
 * Welsh flood sources are capped at NRW's stated 1:5,000 display limit), and
 * without this a farmer could sit past the cap with the toggle on, the legend
 * card drawn, and nothing on the map. Only categories with NO source renderable
 * at currentZoom are considered, so a layer that is already drawing never drags
 * the map out from under a category that is fine. The deepest qualifying maxZoom
 * wins, so the map moves as little as it can and still shows something.
 */
export function computeRequiredZoom(layerStates, viewportRegion, currentZoom) {
  const region = normaliseRegion(viewportRegion);
  if (!region) return null;

  let zoomInTarget = null;
  let zoomOutTarget = null;

  for (const category of CATALOGUE) {
    const state = layerStates[category.id];
    if (!state?.visible) continue;

    const sources = getActiveSources(category, region).filter(isRenderable);

    const anyDrawing = sources.some(
      (source) =>
        currentZoom >= source.minZoom &&
        currentZoom <= (source.maxZoom ?? DEFAULT_MAX_ZOOM),
    );

    for (const source of sources) {
      if (currentZoom < source.minZoom) {
        // This source is toggled on but hidden because zoom is too low.
        // Pick the shallowest minZoom needed to reveal at least one source.
        if (zoomInTarget === null || source.minZoom < zoomInTarget) {
          zoomInTarget = source.minZoom;
        }
        continue;
      }

      // Hidden because the map is zoomed in past the source's cap, and nothing
      // else in this category is covering for it.
      const maxZoom = source.maxZoom ?? DEFAULT_MAX_ZOOM;
      if (!anyDrawing && currentZoom > maxZoom) {
        if (zoomOutTarget === null || maxZoom > zoomOutTarget) {
          zoomOutTarget = maxZoom;
        }
      }
    }
  }

  return zoomInTarget ?? zoomOutTarget;
}

/**
 * Incrementally reconcile the overlays on the map against the desired set.
 * Pure and side-effecting on the passed-in map/instances only — no React or
 * google.maps globals — so it is unit-testable with a fake MVCArray + fake
 * overlays (see useLayerController.test.js). This is the heart of the BBB-423
 * incremental behaviour: existing overlay instances are REUSED across calls
 * (not destroyed and rebuilt), so tiles don't refetch on every zoom/toggle.
 *
 * @param {object} args
 * @param {{overlayMapTypes: {push, removeAt, getArray}}} args.map
 * @param {Map<string, object>} args.instances  sourceId -> overlay instance (mutated)
 * @param {string[]} args.ordered  sourceIds currently on the map, in order
 * @param {Array<{source: {id: string}}>} args.desiredSources  desired, in stacking order
 * @param {(id: string, status: string) => void} [args.setStatus]
 * @param {Map<string, object>} [args.trackers]  sourceId -> health tracker (mutated)
 * @param {(source: object, onOutcome: Function) => object|null} [args.makeOverlay]  overlay factory (DI for tests)
 * @param {(sourceId: string) => object} [args.makeTracker]  health tracker factory (DI for tests)
 * @returns {string[]} the new ordered sourceId list (store into orderedRef.current)
 */
export function reconcileOverlays({
  map,
  instances,
  ordered,
  desiredSources,
  setStatus,
  trackers = new Map(),
  makeOverlay = createOverlay,
  makeTracker = (sourceId) =>
    createHealthTracker({ report: (status) => setStatus?.(sourceId, status) }),
}) {
  const desiredIds = desiredSources.map((d) => d.source.id);
  const desiredSet = new Set(desiredIds);
  const currentSet = new Set(ordered);

  // 1. Remove overlays that are no longer desired.
  for (const sourceId of currentSet) {
    if (!desiredSet.has(sourceId)) {
      const overlay = instances.get(sourceId);
      if (overlay) removeOverlay(map, overlay);
      instances.delete(sourceId);
      // Drop the health timers with the overlay, or a layer turned off while it
      // was still loading would announce itself unavailable seconds later.
      trackers.get(sourceId)?.dispose();
      trackers.delete(sourceId);
    }
  }

  // 2. Create instances for newly desired overlays (don't push yet).
  //
  // Status comes from the tiles, never from getting this far. Until 19/08/2026
  // this line published 'loaded' the moment an overlay was constructed, which is
  // the single reason a dead provider was silent: constructing an ImageMapType
  // says nothing about whether the server behind it answers.
  for (const { source } of desiredSources) {
    if (!instances.has(source.id)) {
      const tracker = makeTracker(source.id);
      const overlay = makeOverlay(source, (outcome) => {
        if (outcome === 'requested') tracker.requested();
        else if (outcome === 'loaded') tracker.succeeded();
        else if (outcome === 'error') tracker.failed();
      });
      if (overlay) {
        instances.set(source.id, overlay);
        trackers.set(source.id, tracker);
      } else {
        // No adapter for this kind: nothing will ever report a tile, so the
        // tracker would sit on timers that can only ever lie.
        tracker.dispose();
      }
    }
  }

  // 3. Reconcile stacking order. Rebuild only when the desired order differs
  //    from what's already on the map, to avoid unnecessary tile churn.
  const currentOrdered = ordered.filter((id) => desiredSet.has(id));
  const orderChanged =
    currentOrdered.length !== desiredIds.length ||
    currentOrdered.some((id, i) => id !== desiredIds[i]);

  if (orderChanged) {
    // Remove all our overlays from the map (removeOverlay is index-safe), then
    // push back in desired order.
    for (const sourceId of ordered) {
      const overlay = instances.get(sourceId);
      if (overlay) removeOverlay(map, overlay);
    }
    for (const sourceId of desiredIds) {
      const overlay = instances.get(sourceId);
      if (overlay) map.overlayMapTypes.push(overlay);
    }
  } else {
    // Order unchanged — push only the newly added overlays.
    const newIds = desiredIds.filter((id) => !currentSet.has(id));
    for (const sourceId of newIds) {
      const overlay = instances.get(sourceId);
      if (overlay) map.overlayMapTypes.push(overlay);
    }
  }

  return [...desiredIds];
}

export function useLayerController({ map, layerStates, viewportRegion, zoom, setStatus }) {
  // Stable map: sourceId → ImageMapType instance. Reused across reconciles so
  // tiles don't refetch on every zoom/toggle.
  const instancesRef = useRef(new Map());
  // Stable map: sourceId -> health tracker, so a layer's timers live exactly as
  // long as its overlay does.
  const trackersRef = useRef(new Map());
  // Ordered array of sourceIds currently pushed onto map.overlayMapTypes.
  const orderedRef = useRef([]);
  // Track previous layerStates to detect toggle changes for the zoom nudge.
  const prevLayerStatesRef = useRef(layerStates);

  // Smooth-zoom effect: when a layer is toggled ON and the map is sitting where
  // that layer cannot draw, ease the map to a zoom where it can. In (the England
  // flood risk layer only renders at ~1:50k / zoom 14+) or out (the Welsh flood
  // sources are capped at zoom 16 by NRW's stated display limit, and clicking a
  // zone puts the map at 18). Deliberately fires on toggle only: nudging on every
  // zoom change would fight a farmer who is zooming the map themselves.
  useEffect(() => {
    if (!map || !window.google?.maps) return;

    const prev = prevLayerStatesRef.current;
    const curr = layerStates;

    const anyNewlyVisible = CATALOGUE.some(
      (cat) => curr[cat.id]?.visible && !prev[cat.id]?.visible,
    );

    if (anyNewlyVisible) {
      const targetZoom = computeRequiredZoom(curr, viewportRegion, zoom);
      if (targetZoom !== null && targetZoom !== zoom) {
        map.setZoom(targetZoom);
      }
    }

    prevLayerStatesRef.current = curr;
  }, [layerStates, map, viewportRegion, zoom]);

  // Teardown effect: remove all owned overlays only when the map instance
  // changes or the component unmounts — NOT on every state change. Keeping this
  // separate from the reconcile effect is what makes reconciliation incremental
  // (BBB-423): the reconcile effect below no longer destroys and rebuilds the
  // whole overlay set on every zoom tick / toggle / region change.
  useEffect(() => {
    if (!map) return undefined;
    const instances = instancesRef.current;
    const trackers = trackersRef.current;
    return () => {
      for (const sourceId of orderedRef.current) {
        const overlay = instances.get(sourceId);
        if (overlay) removeOverlay(map, overlay);
      }
      for (const tracker of trackers.values()) tracker.dispose();
      trackers.clear();
      instances.clear();
      orderedRef.current = [];
    };
  }, [map]);

  // Incremental reconciliation effect (no cleanup — teardown lives above).
  useEffect(() => {
    if (!map || !window.google?.maps) return;

    const desiredSources = collectDesiredSources(layerStates, viewportRegion, zoom);
    orderedRef.current = reconcileOverlays({
      map,
      instances: instancesRef.current,
      trackers: trackersRef.current,
      ordered: orderedRef.current,
      desiredSources,
      setStatus,
    });
  }, [layerStates, map, setStatus, viewportRegion, zoom]);
}
